import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Stack,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Paper as MuiPaper,
    ClickAwayListener,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Grid,
    Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ContentCopy, Visibility, Person, Email, Phone, CheckCircle, Cancel, Event, Update, Person as PersonIcon, Badge, Work } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

interface User {
    username: string;
    enabled: boolean;
    status: 'CONFIRMED' | 'UNCONFIRMED';
    createdAt: string;
    lastModifiedAt: string;
    attributes: {
        email?: string;
        email_verified?: string;
        phone_number?: string;
        phone_number_verified?: string;
        name: string;
        sub: string;
    };
}

const copyToClipboard = async (text: string, setCopied: (value: boolean) => void) => {
    try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
        console.error('Failed to copy text: ', err);
    }
};

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label = 'Copy' }) => {
    const [copied, setCopied] = React.useState(false);

    return (
        <Tooltip title={copied ? 'Copied!' : label}>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(text, setCopied);
                }}
            >
                <ContentCopy fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

interface UserProfile {
    firstname?: string;
    lastname?: string;
    email?: string;
    phone?: string;
    role?: string[];
    createdAt?: string;
    updatedAt?: string;
}

const AppUsersPage: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [profileLoading, setProfileLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const searchRef = React.useRef<HTMLDivElement>(null);

    const searchUsers = async (query: string) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_AUTH_URL}searchUser`, {
                params: { search: query }
            });
            setFilteredUsers(response.data || []);
        } catch (err) {
            console.error('Error searching users:', err);
            setError('Error searching users');
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();

            // If no dates are selected, set default to current month
            const today = new Date();
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

            const start = startDate || firstDayOfMonth;
            const end = endDate || lastDayOfMonth;

            params.append('startDate', start.toISOString().split('T')[0]);
            params.append('endDate', end.toISOString().split('T')[0]);

            const response = await axios.get(`${process.env.REACT_APP_AUTH_URL}listAllUsers?${params.toString()}`);
            const usersData = response.data.users || [];
            setUsers(usersData);
            setFilteredUsers(usersData);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [startDate, endDate]);

    // If handleSearch is meant to be called on button click:
    const handleSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        // Your search logic here
        searchUsers(searchQuery);
    };

    // If you need to handle input changes, use a separate handler:
    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleUserSelect = (user: User) => {
        setSearchQuery(getUserIdentifier(user));
        setFilteredUsers([user]);
        setIsSearchFocused(false);
    };

    const fetchUserProfile = async (userId: string) => {
        try {
            setProfileLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_CLINICAL_URL}/Profile/${userId}`);
            if (response.status === 200 && response.data) {
                setUserProfile(response.data);
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
            // Fallback to default user data if API fails
            setUserProfile({
                firstname: selectedUser?.attributes?.name?.split(' ')[0] || '',
                lastname: selectedUser?.attributes?.name?.split(' ').slice(1).join(' ') || '',
                email: selectedUser?.attributes?.email || '',
                phone: selectedUser?.attributes?.phone_number || '',
                role: ['user']
            });
        } finally {
            setProfileLoading(false);
        }
    };

    const handleViewProfile = (user: User) => {
        setSelectedUser(user);
        setOpenDialog(true);
        fetchUserProfile(user.username);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setFilteredUsers(users);
    };

    const handleReset = () => {
        setStartDate(null);
        setEndDate(null);
        fetchUsers();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const getUserIdentifier = (user: User) => {
        return user.attributes.email || user.attributes.phone_number || user.attributes.name;
    };

    const isVerified = (user: User) => {
        return user.attributes.email_verified === 'true' ||
            user.attributes.phone_number_verified === 'true';
    };

    return (
        <Box sx={{ p: 3, pt: 1.5 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontFamily: "Libre Baskerville", color: "#3B362A" }}>
                    App Users
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Manage and view all app users
                </Typography>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ position: 'relative', mb: 3 }} ref={searchRef}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search users by name, email, or phone..."
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onFocus={() => setIsSearchFocused(true)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        ✕
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { bgcolor: 'background.paper' }
                        }}
                    />
                    {isSearchFocused && searchQuery.length > 2 && (
                        <ClickAwayListener onClickAway={() => setIsSearchFocused(false)}>
                            <MuiPaper
                                elevation={3}
                                sx={{
                                    position: 'absolute',
                                    width: '100%',
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    mt: 0.5,
                                    zIndex: 1
                                }}
                            >
                                <List>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user) => (
                                            <ListItem key={user.username} disablePadding>
                                                <ListItemButton onClick={() => handleUserSelect(user)}>
                                                    <ListItemText
                                                        primary={getUserIdentifier(user)}
                                                        secondary={user.attributes.name}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))
                                    ) : (
                                        <ListItem>
                                            <ListItemText primary="No users found" />
                                        </ListItem>
                                    )}
                                </List>
                            </MuiPaper>
                        </ClickAwayListener>
                    )}
                </Box>

                <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
                    <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={setStartDate}
                        slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
                    />
                    <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={setEndDate}
                        slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
                    />

                </Stack>
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ minWidth: 100 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Search'}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                    sx={{ minWidth: 100 }}
                >
                    Reset
                </Button>
            </LocalizationProvider>

            {
                error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )
            }

            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>User</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Verified</TableCell>
                               
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : users.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No users found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsers.map((user) => (
                                    <TableRow key={user.username} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {user.username}
                                                <CopyButton text={user.username} label="Copy ID" />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                    {getUserIdentifier(user)}
                                                    <CopyButton text={getUserIdentifier(user)} />
                                                </Box>
                                                <div style={{ fontSize: '0.8em', color: 'gray' }}>
                                                    {user.attributes.name}
                                                </div>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {user.attributes.email ? 'Email' : 'Phone'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.status}
                                                color={user.status === 'CONFIRMED' ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={isVerified(user) ? 'Yes' : 'No'}
                                                color={isVerified(user) ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                       
                                        <TableCell>
                                            <Tooltip title="View Profile">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewProfile(user);
                                                    }}
                                                >
                                                    <Visibility fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="md"
                fullWidth
            >
                {selectedUser && (
                    <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                <Person />
                            </Avatar>
                            User Profile
                        </DialogTitle>
                        <DialogContent dividers>
                            {profileLoading ? (
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Basic Information
                                            </Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Person color="action" />
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Full Name
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {userProfile?.firstname || selectedUser?.attributes?.name || 'N/A'}
                                                        {userProfile?.lastname && ` ${userProfile.lastname}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Email color="action" />
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Email
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {userProfile?.email || selectedUser?.attributes?.email || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                <Phone color="action" />
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Phone
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {userProfile?.phone || selectedUser?.attributes?.phone_number || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Account Status
                                            </Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                {selectedUser.status === 'CONFIRMED' ? (
                                                    <CheckCircle color="success" />
                                                ) : (
                                                    <Cancel color="error" />
                                                )}
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Account Status
                                                    </Typography>
                                                    <Chip
                                                        label={selectedUser.status}
                                                        color={selectedUser.status === 'CONFIRMED' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                {isVerified(selectedUser) ? (
                                                    <CheckCircle color="success" />
                                                ) : (
                                                    <Cancel color="error" />
                                                )}
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Email Verified
                                                    </Typography>
                                                    <Chip
                                                        label={selectedUser.attributes.email_verified === 'true' ? 'Verified' : 'Not Verified'}
                                                        color={selectedUser.attributes.email_verified === 'true' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>
                                            {selectedUser.attributes.phone_number && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                    {selectedUser.attributes.phone_number_verified === 'true' ? (
                                                        <CheckCircle color="success" />
                                                    ) : (
                                                        <Cancel color="error" />
                                                    )}
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Phone Verified
                                                        </Typography>
                                                        <Chip
                                                            label={selectedUser.attributes.phone_number_verified === 'true' ? 'Verified' : 'Not Verified'}
                                                            color={selectedUser.attributes.phone_number_verified === 'true' ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Account Details
                                            </Typography>
                                            <Divider sx={{ my: 1 }} />
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                        <Event color="action" />
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Created At
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {formatDate(selectedUser.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                        <Update color="action" />
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Last Modified
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {formatDate(selectedUser.lastModifiedAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                User ID
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                                    {selectedUser.username}
                                                                </Typography>
                                                                <CopyButton text={selectedUser.username} />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    if (selectedUser) {
                                        navigate('/supplement-orders', { state: { userId: selectedUser.username } });
                                    }
                                }}
                                color="primary"
                                variant="contained"
                                sx={{ mr: 1 }}
                            >
                                Order Supplements
                            </Button>
                            <Button
                                onClick={() => {
                                    setOpenDialog(false);
                                    setUserProfile(null);
                                }}
                                color="primary"
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default AppUsersPage;